import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Typography
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'

import { Trash2 } from 'lucide-react'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { ContactSelect } from '../ContactSelect'
import { InputField } from '../InputField'
import { InputFieldText } from '../InputFieldText'
import SelectInput from '../SelectInput'
import { useStyles } from './style'

const ClienteSchema = Yup.object().shape({
  nome: Yup.string()
    .min(2, 'Muito curto!')
    .max(100, 'Muito longo!')
    .required('Nome é obrigatório'),
  cpfCnpj: Yup.string()
    .min(11, 'CPF/CNPJ inválido')
    .max(18, 'CPF/CNPJ inválido')
    .required('CPF/CNPJ é obrigatório'),
  tipo: Yup.string().required('Tipo é obrigatório')
})

const initialState = {
  nome: '',
  cpfCnpj: '',
  tipo: 'Pessoa Física',
  segmento: ''
}

const ClienteModal = ({ open, onClose, clienteId, onSave }) => {
  const classes = useStyles()

  const [cliente, setCliente] = useState(initialState)
  const [contacts, setContacts] = useState([])
  const [selectedContact, setSelectedContact] = useState(null)
  const [funcao, setFuncao] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchClient = async () => {
      if (!open) return
      if (!clienteId) {
        setCliente(initialState)
        setContacts([])
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        const { data } = await api.get(`/clientes/${clienteId}`)
        setCliente(prevState => {
          return { ...prevState, ...data }
        })
        // Buscar contatos do cliente
        const { data: contactsData } = await api.get(
          `/clientes/${clienteId}/contacts`
        )
        setContacts(contactsData)
      } catch (err) {
        toastError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchClient()
  }, [clienteId, open])

  const handleClose = () => {
    onClose()
    setCliente(initialState)
    setContacts([])
    setSelectedContact(null)
    setFuncao('')
  }

  const handleAddContact = async () => {
    console.log('DEBUG - Tentando salvar contato vinculado:', {
      clienteId,
      contactId: selectedContact,
      funcao: funcao.trim()
    })
    if (!selectedContact || !funcao.trim()) {
      toast.warning('Selecione um contato e informe a função')
      return
    }

    if (!clienteId) {
      toast.warning('Salve o cliente antes de adicionar contatos')
      return
    }

    setLoading(true)
    try {
      await api.post('/clientes/contacts', {
        clienteId: clienteId,
        contactId: selectedContact,
        funcao: funcao
      })

      // Recarregar lista de contatos
      const { data: contactsData } = await api.get(
        `/clientes/${clienteId}/contacts`
      )
      setContacts(contactsData)

      setSelectedContact(null)
      setFuncao('')
      toast.success('Contato adicionado com sucesso')
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveContact = async contactId => {
    if (!clienteId) return

    setLoading(true)
    try {
      await api.delete(`/clientes/${clienteId}/contacts/${contactId}`)
      setContacts(contacts.filter(c => c.contactId !== contactId))
      toast.success('Contato removido com sucesso')
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveCliente = async values => {
    const clienteData = { ...values }
    try {
      if (clienteId) {
        await api.put(`/clientes/${clienteId}`, clienteData)
        toast.success(i18n.t('clienteModal.success.update'))
      } else {
        await api.post('/clientes', clienteData)
        toast.success(i18n.t('clienteModal.success.create'))
      }
      if (typeof onSave === 'function') {
        onSave()
      }
      handleClose()
    } catch (err) {
      toastError(err)
    }
  }

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          {clienteId
            ? `${i18n.t('clienteModal.title.edit')}`
            : `${i18n.t('clienteModal.title.add')}`}
        </DialogTitle>
        <Formik
          initialValues={cliente}
          enableReinitialize={true}
          validationSchema={ClienteSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCliente(values)
              actions.setSubmitting(false)
            }, 400)
          }}
        >
          {({ touched, errors, isSubmitting, values, handleChange }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InputField
                      label={i18n.t('clienteModal.form.nome')}
                      autoFocus
                      name="nome"
                      error={touched.nome && Boolean(errors.nome)}
                      helperText={touched.nome && errors.nome}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.inputWrapper}>
                    <Grid item xs={6}>
                      <SelectInput
                        label={i18n.t('clienteModal.form.tipo')}
                        name="tipo"
                        labelId="tipo-label"
                        value={values.tipo}
                        onChange={handleChange}
                        id="tipo"
                        error={touched.tipo && Boolean(errors.tipo)}
                      >
                        <MenuItem value="Pessoa Física">Pessoa Física</MenuItem>
                        <MenuItem value="Pessoa Jurídica">
                          Pessoa Jurídica
                        </MenuItem>
                      </SelectInput>
                    </Grid>
                    <Grid item xs={6}>
                      <InputField
                        label={i18n.t('clienteModal.form.cpfCnpj')}
                        name="cpfCnpj"
                        error={touched.cpfCnpj && Boolean(errors.cpfCnpj)}
                        helperText={touched.cpfCnpj && errors.cpfCnpj}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <InputField
                      label={i18n.t('clienteModal.form.segmento')}
                      name="segmento"
                      error={touched.segmento && Boolean(errors.segmento)}
                      helperText={touched.segmento && errors.segmento}
                    />
                  </Grid>
                </Grid>

                {clienteId && (
                  <div className={classes.contactContainer}>
                    <Typography variant="subtitle1">
                      Contatos Vinculados
                    </Typography>
                    <Grid container className={classes.contactWrapper}>
                      <Grid
                        item
                        xs={12}
                        style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'end'
                        }}
                      >
                        <Grid item xs={7}>
                          <ContactSelect
                            label="Selecionar Contato"
                            onSelected={contact => {
                              // LOG DE SELEÇÃO: Verifica o que o componente de busca retorna
                              console.log(
                                'DEBUG - Contato selecionado no ContactSelect:',
                                contact
                              )
                              setSelectedContact(contact ? contact.id : null)
                            }}
                            popupIcon={null}
                            allowCreate={false}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <InputFieldText
                            label="Função"
                            value={funcao}
                            onChange={e => {
                              // LOG DE DIGITAÇÃO: Verifica se o evento de input está chegando
                              console.log(
                                'DEBUG - Digitando função:',
                                e.target.value
                              )
                              setFuncao(e.target.value)
                            }}
                            placeholder="Ex: Gerente, Diretor"
                          />
                        </Grid>
                        <Grid item xs={1} className={classes.iconWrapper}>
                          <IconButton
                            className={classes.iconButton}
                            onClick={handleAddContact}
                            disabled={
                              loading || !selectedContact || !funcao.trim()
                            }
                          >
                            <AddIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                      {contacts.length > 0 ? (
                        <Paper className={classes.paper} elevation={0}>
                          <List className={classes.list}>
                            {contacts.map((contact, index) => (
                              <ListItem key={contact.id}>
                                <ListItemText
                                  className={classes.listItemText}
                                  primary={
                                    <Typography
                                      variant="span"
                                      className={classes.contactName}
                                    >
                                      {contact.contact?.name || 'Sem nome'}
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      variant="caption"
                                      className={classes.functionNameAndPhone}
                                    >
                                      <strong>
                                        {contact.funcao || 'Sem função'}
                                      </strong>
                                      {` — ${contact.contact?.number || 'Sem número'}`}
                                    </Typography>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() =>
                                      handleRemoveContact(contact.contactId)
                                    }
                                    className={classes.iconButtonDelete}
                                  >
                                    <Trash2 size={18} />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nenhum contato vinculado ainda.
                        </Typography>
                      )}
                    </Grid>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <ButtonSecondary
                  onClick={handleClose}
                  disabled={isSubmitting}
                  label={i18n.t('clienteModal.buttons.cancel')}
                />

                <ButtonPrimary
                  type="submit"
                  disabled={isSubmitting}
                  className={classes.btnWrapper}
                  label={
                    clienteId
                      ? `${i18n.t('clienteModal.buttons.okEdit')}`
                      : `${i18n.t('clienteModal.buttons.okAdd')}`
                  }
                />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  )
}

export default ClienteModal
